import { useId, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import type { TransportMode } from '../lib/sl.ts';

const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      data-card=""
      className={cx('bg-raised border-line rounded-card border', className)}
    >
      {children}
    </section>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger';
};

export function Button({ variant = 'primary', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        // 44px minimum: phone app, thumb targets.
        'min-h-11 rounded-xl px-4 text-[15px] font-semibold transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-2',
        variant === 'primary' && 'bg-accent text-raised active:brightness-95',
        variant === 'ghost' && 'text-ink border-line border-2 active:bg-surface',
        variant === 'danger' &&
          'text-danger border-danger/50 border-2 active:bg-danger/10',
        className,
      )}
    />
  );
}

/**
 * The line number, in its mode's colour.
 *
 * Written out as whole class names rather than composed as `bg-${mode}`.
 * Tailwind finds classes by scanning source text, so a name assembled at
 * runtime is a name it never sees -- the utility is simply not generated and
 * the badge renders with no fill at all.
 *
 * Fixed minimum width rather than hugging the text, so a column of badges
 * lines up whether the line is "4" or "43X"; the destinations beside them
 * then start at the same x and the card scans vertically.
 */
const MODE_CLASS: Record<string, string> = {
  BUS: 'bg-bus',
  METRO: 'bg-metro',
  TRAIN: 'bg-train',
  TRAM: 'bg-tram',
  SHIP: 'bg-ship',
  FERRY: 'bg-ship',
};

export function LineBadge({
  line,
  mode,
  className,
}: {
  line: string;
  mode: TransportMode | string;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'nums inline-flex min-w-11 items-center justify-center rounded-lg px-1.5',
        'py-1 text-[15px] leading-none font-bold text-badge-ink',
        MODE_CLASS[mode] ?? 'bg-metro',
        className,
      )}
    >
      {line}
    </span>
  );
}

export function Field({
  label,
  hint,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="block">
      <label htmlFor={id} className="text-muted mb-1.5 block text-[13px] font-medium">
        {label}
      </label>
      <input
        {...props}
        id={id}
        aria-describedby={hint ? hintId : undefined}
        className={cx(
          'bg-surface border-line text-ink min-h-11 w-full rounded-xl border px-3',
          'placeholder:text-faint focus:border-accent focus:outline-none',
          className,
        )}
      />
      {hint && (
        <span id={hintId} className="text-faint mt-1 block text-xs">
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * A placeholder occupying the space its content will take.
 *
 * Everything the board can know without asking SL -- your stops, line numbers,
 * destinations, which stand each uses -- is drawn from saved config on the
 * first paint. This stands in for the one part that genuinely has to be
 * fetched, so the row is already its final size and the times appear in place
 * rather than pushing the page around.
 *
 * Display is left to the caller. Sitting a bar inside a span that carries the
 * same font size as the real content is what makes the placeholder occupy an
 * identical line box -- see the board's time cells, where a bare block would
 * collapse the row by the difference between a 12px bar and a 17px line.
 *
 * The pulse is animated, so the reduced-motion rule in styles.css stops it
 * along with everything else; it then reads as a plain grey bar, which is
 * still the right shape.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx('bg-line h-3 animate-pulse rounded-full', className)}
    />
  );
}

/**
 * A short warning attached to a departure row.
 *
 * Lateness used to live in a `title` attribute, which on a phone is no
 * attribute at all -- the only signal was the time changing colour, with
 * nothing saying why. Anything worth knowing is said in words, under the row
 * it belongs to.
 */
/**
 * A collapsible section.
 *
 * Built on <details>, which brings keyboard operation, the right screen
 * reader announcement and find-in-page expansion without any of that being
 * written here.
 *
 * Open state can be controlled -- the board remembers which stops you
 * collapsed -- or left to the element, which is what the pickers do, since
 * nobody wants last week's scroll position restored.
 */
export function Disclosure({
  summary,
  children,
  open: controlled,
  onOpenChange,
  defaultOpen = true,
  className,
  summaryClassName,
}: {
  summary: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  className?: string;
  summaryClassName?: string;
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = controlled ?? uncontrolled;

  return (
    <details
      className={cx('group', className)}
      open={open}
      onToggle={(e) => {
        const next = e.currentTarget.open;
        if (next === open) return;
        if (controlled === undefined) setUncontrolled(next);
        onOpenChange?.(next);
      }}
    >
      <summary
        className={cx(
          'flex cursor-pointer list-none items-center gap-3',
          'focus-visible:outline-accent focus-visible:outline-2 focus-visible:-outline-offset-2',
          '[&::-webkit-details-marker]:hidden',
          summaryClassName,
        )}
      >
        {summary}
        <svg
          viewBox="0 0 24 24"
          className="text-faint size-4 shrink-0 transition-transform group-open:rotate-180"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </summary>
      {children}
    </details>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'late';
  children: ReactNode;
}) {
  return (
    <p
      className={cx(
        'flex items-start gap-1.5 rounded-lg px-2 py-1 text-[13px]',
        tone === 'late' ? 'bg-late/12 text-late' : 'bg-surface text-muted',
      )}
    >
      <svg
        viewBox="0 0 24 24"
        className="mt-px size-3.5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {tone === 'late' ? (
          <>
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </>
        ) : (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </>
        )}
      </svg>
      <span>{children}</span>
    </p>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="text-faint flex items-center justify-center gap-2 py-10 text-sm">
      <span
        aria-hidden
        className="border-line border-t-accent size-4 animate-spin rounded-full border-2"
      />
      {label}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="py-10 text-center">
      <p className="text-muted text-sm font-medium">{title}</p>
      {children && <div className="text-faint mt-1 text-[13px]">{children}</div>}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-danger text-[13px]">
      {children}
    </p>
  );
}
